/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { getBankAccounts, getBanksList, getCurrenciesList, addBankAccount, updateBankAccount, deleteBankAccount } from "../../../api/signalApi";
import MyTable from "../../../components/MyTable";
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MyTextField from "../../../components/MyTextField";
import MySelect from "../../../components/MySelect";
import MySnackbar from "../../../components/MySnackbar";
import { resetOperationState } from "../../../store/slices/bank/bankSlice";

const ACCOUNT_TYPE_OPTIONS = [
    { Text: "Corriente", Value: "C" },
    { Text: "Ahorros", Value: "A" },
    { Text: "Maestra", Value: "M" },
    { Text: "CTS", Value: "T" },
    { Text: "Detracción", Value: "D" },
    { Text: "Cuenta Sueldo", Value: "S" }
];

export const BankAccountsTab = () => {
    const dispatch = useDispatch();
    const { companyselected, connectionId } = useSelector((state: RootState) => state.auth);
    const { accounts, banks, currencies, loading, operationSuccess, operationMsg } = useSelector((state: RootState) => state.bank);

    const [openModal, setOpenModal] = useState(false);
    const [operation, setOperation] = useState<0 | 1 | 2>(0); // 0: Add, 1: Edit, 2: Delete

    const [selectedId, setSelectedId] = useState<string>("");
    const [bankId, setBankId] = useState<string>("");
    const [accountType, setAccountType] = useState<string>("");
    const [accountNumber, setAccountNumber] = useState<string>("");
    const [chargeAccountNumber, setChargeAccountNumber] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [currency, setCurrency] = useState<string>("");
    const [interbankAccount, setInterbankAccount] = useState<string>("");
    const [isActive, setIsActive] = useState<boolean>(true);
    const [isDefault, setIsDefault] = useState<boolean>(false);

    useEffect(() => {
        if (!companyselected?.Token || !connectionId) return;
        getBankAccounts();
        if (banks.length === 0) getBanksList();
        if (currencies.length === 0) getCurrenciesList();
    }, [companyselected?.Token, connectionId]);

    useEffect(() => {
        if (operationSuccess !== null) {
            if (operationSuccess) {
                getBankAccounts();
                setOpenModal(false);
                resetForm();
            }
            setTimeout(() => {
                dispatch(resetOperationState());
            }, 3000);
        }
    }, [operationSuccess, dispatch]);

    const resetForm = () => {
        setSelectedId("");
        setBankId("");
        setAccountType("");
        setAccountNumber("");
        setChargeAccountNumber("");
        setDescription("");
        setCurrency("");
        setInterbankAccount("");
        setIsActive(true);
        setIsDefault(false);
    }

    const handleFormSubmit = async () => {
        const payload = {
            NroIdentificadorCuentaBanco: selectedId,
            Banco_ID: bankId,
            TipoCuentaBanco: accountType,
            NoCuenta: accountNumber,
            NoCuentaCargo: chargeAccountNumber,
            Descripcion: description,
            CodMoneda: currency,
            CuentaInterBancaria: interbankAccount,
            Activo: isActive ? 1 : 0,
            Predeterminado: isDefault ? 1 : 0
        };

        if (operation === 0) {
            await addBankAccount(payload);
        } else if (operation === 1) {
            await updateBankAccount(payload);
        } else if (operation === 2) {
            await deleteBankAccount(selectedId);
        }
    };

    const handleEdit = (row: any) => {
        setOperation(1);
        setSelectedId(row.NroIdentificadorCuentaBanco);
        setBankId(row.Banco_ID);
        setAccountType(ACCOUNT_TYPE_OPTIONS.find(t => t.Text === row.TipoCuentaBanco)?.Value || '');
        setAccountNumber(row.NoCuenta.replace(/\D/g, ''));
        setChargeAccountNumber(row.NoCuentaCargo ? row.NoCuentaCargo.replace(/\D/g, '') : '');
        setDescription(row.Descripcion);
        setCurrency(row.CodMoneda);
        setInterbankAccount(row.CuentaInterBancaria ? row.CuentaInterBancaria.replace(/\D/g, '') : '');
        setIsActive(row.Activo);
        setIsDefault(row.Predeterminado);
        setOpenModal(true);
    };

    const handleDelete = (row: any) => {
        setOperation(2);
        setSelectedId(row.NroIdentificadorCuentaBanco);
        setBankId(row.Banco_ID);
        setAccountType(row.TipoCuentaBanco);
        setAccountNumber(row.NoCuenta);
        setOpenModal(true);
    };

    return (
        <>
            <MyTable
                key="bank-accounts-table"
                tableHeadRows={[
                    { label: "Banco", rowKeyName: "BancoDescripcion", allowOrderAction: false, customValue: (_head, row) => banks.find(b => b.Banco_ID === row.Banco_ID)?.Descripcion || row.Banco_ID },
                    { label: "Tipo", rowKeyName: "TipoCuentaBanco", allowOrderAction: false, customValue: (_head, row) => ACCOUNT_TYPE_OPTIONS.find(t => t.Value === row.TipoCuentaBanco)?.Text || row.TipoCuentaBanco },
                    { label: "Modena", rowKeyName: "CodMoneda", allowOrderAction: false, customValue: (_head, row) => currencies.find(c => c.CodMoneda === row.CodMoneda)?.Descripcion || row.CodMoneda },
                    { label: "Nro Cuenta", rowKeyName: "NoCuenta", allowOrderAction: false },
                    { label: "Descripción", rowKeyName: "Descripcion", allowOrderAction: false },
                    {
                        label: "Acciones",
                        rowKeyName: "actions",
                        customValue: (_head, row) => (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <IconButton onClick={() => handleEdit(row)} color="primary">
                                    <EditRoundedIcon />
                                </IconButton>
                                <IconButton onClick={() => handleDelete(row)} color="error">
                                    <DeleteRoundedIcon />
                                </IconButton>
                            </div>
                        )
                    }
                ]}
                tableRows={accounts}
                loading={loading}
                customItemOptionBar={() => (
                    <Grid>
                        <Button
                            variant="outlined"
                            style={{ backgroundColor: "white", fontSize: "14px" }}
                            startIcon={<AddIcon />}
                            onClick={() => {
                                resetForm();
                                setOperation(0);
                                setOpenModal(true);
                            }}
                        >
                            Agregar
                        </Button>
                    </Grid>
                )}
                hiddenSearchInput={true}
                hiddenBottomPaginator={true}
                tableOptionsBar={{ useDrawer: false }}
            />

            <Dialog open={openModal} maxWidth="md" fullWidth>
                <DialogTitle style={{ textAlign: 'center', marginTop: '0.5rem', paddingBottom: 0 }}>
                    {operation === 0 ? "Agregar Cuenta Bancaria" : operation === 1 ? "Modificar Cuenta Bancaria" : "Eliminar Cuenta Bancaria"}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} style={{ marginTop: '0.1rem' }}>
                        {operation !== 2 ? (
                            <>
                                <Grid item xs={12} md={6}>
                                    <MySelect
                                        label="Banco"
                                        value={bankId}
                                        onChange={(e) => setBankId(e.target.value)}
                                    >
                                        {banks.map((b) => (
                                            <MenuItem key={b.Banco_ID} value={b.Banco_ID}>{b.Descripcion}</MenuItem>
                                        ))}
                                    </MySelect>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MySelect
                                        label="Tipo de Cuenta"
                                        value={accountType}
                                        onChange={(e) => setAccountType(e.target.value)}
                                    >
                                        {ACCOUNT_TYPE_OPTIONS.map((t) => (
                                            <MenuItem key={t.Value} value={t.Value}>{t.Text}</MenuItem>
                                        ))}
                                    </MySelect>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MySelect
                                        label="Moneda"
                                        value={currency}
                                        onChange={(e) => setCurrency(e.target.value)}
                                    >
                                        {currencies.map((c) => (
                                            <MenuItem key={c.CodMoneda} value={c.CodMoneda}>{c.Descripcion}</MenuItem>
                                        ))}
                                    </MySelect>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MyTextField
                                        label="Nro Cuenta"
                                        value={accountNumber}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '');
                                            setAccountNumber(val);
                                            if (operation === 0) setChargeAccountNumber(val);
                                        }}
                                        type="tel"
                                        style={{ width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MyTextField
                                        label="Nro Cuenta Cargo"
                                        value={chargeAccountNumber}
                                        onChange={(e) => setChargeAccountNumber(e.target.value.replace(/\D/g, ''))}
                                        type="tel"
                                        style={{ width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MyTextField
                                        label="CCI (Cuenta Interbancaria)"
                                        value={interbankAccount}
                                        onChange={(e) => setInterbankAccount(e.target.value.replace(/\D/g, ''))}
                                        type="tel"
                                        style={{ width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <MyTextField
                                        label="Descripción"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value.toUpperCase())}
                                        style={{ width: '100%' }}
                                    />
                                </Grid>
                            </>
                        ) : (
                            <Grid item xs={12}>
                                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                                    ¿Estás seguro de eliminar la siguiente cuenta?
                                </div>
                                <Grid container spacing={1} justifyContent="center" style={{ backgroundColor: '#f5f5f5', padding: '1rem', borderRadius: '4px' }}>
                                    <Grid item xs={6} style={{ textAlign: 'right', fontWeight: 'bold' }}>Banco:</Grid>
                                    <Grid item xs={6}>{banks.find(b => b.Banco_ID === bankId)?.Descripcion || bankId}</Grid>

                                    <Grid item xs={6} style={{ textAlign: 'right', fontWeight: 'bold' }}>Tipo:</Grid>
                                    <Grid item xs={6}>{ACCOUNT_TYPE_OPTIONS.find(t => t.Value === accountType)?.Text || accountType}</Grid>

                                    <Grid item xs={6} style={{ textAlign: 'right', fontWeight: 'bold' }}>Moneda:</Grid>
                                    <Grid item xs={6}>{currencies.find(c => c.CodMoneda === currency)?.Descripcion || currency}</Grid>

                                    <Grid item xs={6} style={{ textAlign: 'right', fontWeight: 'bold' }}>Cuenta:</Grid>
                                    <Grid item xs={6}>{accountNumber}</Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions style={{ margin: '1rem' }}>
                    <Button onClick={() => setOpenModal(false)} variant="outlined" startIcon={<CloseIcon />}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleFormSubmit}
                        variant="contained"
                        color={operation === 2 ? "error" : "primary"}
                        startIcon={<CheckIcon />}
                    >
                        {operation === 2 ? "Eliminar" : "Guardar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {operationSuccess !== null && <MySnackbar
                open={true}
                severity={operationSuccess ? "success" : "error"}
                msg={operationMsg}
                onClose={() => dispatch(resetOperationState())}
            />}
        </>
    );
};
